import React, { useEffect, useRef, useState } from 'react';
import './Section.scss';


const Section = ({ sectionType, children }) => {

  // State to track if Calendly is ready
  const [calendlyReady, setCalendlyReady] = useState(false);

  // Check for Calendly availability
  useEffect(() => {
    const checkCalendly = () => {
      if (typeof window !== 'undefined' && window.Calendly && window.Calendly.initPopupWidget) {
        setCalendlyReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkCalendly()) {
      return;
    }

    // If not ready, poll for it
    const interval = setInterval(() => {
      if (checkCalendly()) {
        clearInterval(interval);
      }
    }, 100);

    // Clean up after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      console.warn('Calendly failed to load within 10 seconds');
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Helper function to safely open Calendly widget
  const openCalendlyWidget = (url) => {
    // Check if Calendly is loaded and available
    if (calendlyReady && typeof window !== 'undefined' && window.Calendly && window.Calendly.initPopupWidget) {
      try {
        window.Calendly.initPopupWidget({ url });
      } catch (error) {
        console.warn('Calendly widget failed to open:', error);
        // Fallback: open in new tab
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } else {
      console.warn('Calendly not ready, opening in new tab');
      // Fallback: open in new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Custom hook for intersection observer
  const useIntersectionObserver = (options = {}) => {
    const [hasBeenVisible, setHasBeenVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setHasBeenVisible(true);
          }
        },
        {
          threshold: 0.1,
          rootMargin: '50px',
          ...options,
        }
      );

      const currentRef = ref.current;
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef);
        }
      };
    }, [options]);

    return [ref, hasBeenVisible];
  };

  const SplashSection = () => {
    return (
        <div id="splash" className="splash_section">
            {/* Overlay */}
            <div className="splash_section_overlay"></div>
            {/* Text */}
            <div className="splash_section_text">
                <p>Helping Women in Vocational Ministry Live a Kingdom Life</p>
            </div>
        </div>
    )
  }

  const WelcomeSection = () => {
    const [ref, hasBeenVisible] = useIntersectionObserver();
    
    return (
        <div ref={ref} id="welcome" className={`welcome_section ${hasBeenVisible ? 'visible' : ''}`}>
            <div className="welcome_section_content">
                <div className="welcome_section_header">
                    <h2 className="welcome_section_title">Welcome</h2>
                    <p className="welcome_section_greeting">I am grateful you are here.</p>
                </div>
                
                <div className="welcome_section_body">
                    <div className="welcome_section_card">
                        <h3 className="welcome_section_subtitle">Are We a Good Fit?</h3>
                        <p>We might be a good fit if you are a woman currently in vocational ministry or going through a period of discernment or reflection about your calling. I work best with committed Christ-followers who are seeking to set apart time and space to hear from God.</p>
                    </div>

                    <div className="welcome_section_card">
                        <h3 className="welcome_section_subtitle">My Approach</h3>
                        <p>I believe Spiritual Direction is a lifelong practice between a director and client, becoming a regular time to trace the story God is writing in your life. This routine lays the foundation for God's shaping work.</p>
                    </div>
                </div>
            </div>
        </div>
    )
  }

  const QualificationsSection = () => {
    const [ref, hasBeenVisible] = useIntersectionObserver();
    const qualifications = [
      "Anglican priest and Spiritual Director in the Diocese of Churches for the Sake of Others",
      "30+ years in vocational ministry at Christ Church Anglican, Overland Park, KS",
      "Ordained priest since 2009",
      "Former Canon for Ordinations under Bishop Todd Hunter",
      "25 years as Executive Pastor at a flourishing 1,000+ member church",
      "Spiritual Direction accreditation from the Abbey of the Heights, Tulsa, Oklahoma"
    ];

    return (
        <div ref={ref} id="qualifications" className={`qualifications_section ${hasBeenVisible ? 'visible' : ''}`}>
            <div className="qualifications_section_content">
                <h2 className="qualifications_section_title">My Qualifications</h2>
                
                <ul className="qualifications_section_list">
                    {qualifications.map((qualification, index) => (
                        <li 
                            key={index} 
                            className="qualifications_section_item"
                            style={{ animationDelay: `${0.8 + (index * 0.2)}s` }}
                        >
                            {qualification}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
  }

  const LetsMeetSection = () => {
    const [ref, hasBeenVisible] = useIntersectionObserver();
    const questions = [
      "Tell me about yourself.",
      "What brings you to Spiritual Direction?",
      "Have you ever received spiritual direction before? If so, how was it?",
      "What are the ways that you have heard from God in the past?",
      "What are your goals for our time together?",
      "What do you want to know about me?"
    ];

    return (
        <div ref={ref} id="letsmeet" className={`letsmeet_section ${hasBeenVisible ? 'visible' : ''}`}>
            <div className="letsmeet_section_content">
                <div className="letsmeet_section_image">
                    <img 
                        src="/assets/sd-studio-trish.jpeg" 
                        alt="Trish Nelson"
                        className="letsmeet_section_photo"
                    />
                </div>
                
                <h2 className="letsmeet_section_title">Let's Meet</h2>
                
                <div className="letsmeet_section_intro">
                    <p>If you've read this and want to move on to a face-to-face, schedule a free 30-minute introduction either online or in-person, whichever you prefer.</p>
                    
                    <div className="letsmeet_section_buttons">
                        <button 
                            className="letsmeet_section_button letsmeet_section_button--zoom"
                            onClick={() => {
                                openCalendlyWidget('https://calendly.com/trishnelson-sd/30-minute-zoom-intro-meeting');
                                return false;
                            }}
                            disabled={!calendlyReady}
                            title={!calendlyReady ? 'Loading scheduling widget...' : 'Schedule a Zoom meeting'}
                        >
                            Meet with Zoom
                        </button>
                        
                        <button 
                            className="letsmeet_section_button letsmeet_section_button--inperson"
                            onClick={() => {
                                openCalendlyWidget('https://calendly.com/trishnelson-sd/30min');
                                return false;
                            }}
                            disabled={!calendlyReady}
                            title={!calendlyReady ? 'Loading scheduling widget...' : 'Schedule an in-person meeting'}
                        >
                            Meet in Person
                        </button>
                    </div>
                </div>

                <div className="letsmeet_section_questions">
                    <h3 className="letsmeet_section_subtitle">When we meet, I will ask you these questions:</h3>
                    
                    <ul className="letsmeet_section_list">
                        {questions.map((question, index) => (
                            <li 
                                key={index} 
                                className="letsmeet_section_item"
                                style={{ animationDelay: `${1.0 + (index * 0.15)}s` }}
                            >
                                {question}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    )
  }

  const MovingForwardSection = () => {
    const [ref, hasBeenVisible] = useIntersectionObserver();
    
    return (
        <div ref={ref} id="movingforward" className={`movingforward_section ${hasBeenVisible ? 'visible' : ''}`}>
            <div className="movingforward_section_content">
                <h2 className="movingforward_section_title">Moving Forward</h2>
                
                {/* Non-Christ Church Network Section */}
                <div className="movingforward_section_card">
                    <h3 className="movingforward_section_subtitle">General Spiritual Direction Sessions</h3>
                    <p className="movingforward_section_text">
                        If we've already met on an intro call, and you're ready to sign up for Spiritual Direction sessions ($75). 
                        You will be taken to a Calendly page to plan your session and pay.
                    </p>
                    
                    <div className="movingforward_section_buttons">
                        <button 
                            className="movingforward_section_button movingforward_section_button--zoom"
                            onClick={() => {
                                openCalendlyWidget('https://calendly.com/trishnelson-sd/50-minute-zoom-meeting');
                                return false;
                            }}
                            disabled={!calendlyReady}
                            title={!calendlyReady ? 'Loading scheduling widget...' : 'Schedule a Zoom session'}
                        >
                            Session with Zoom
                        </button>
                        
                        <button 
                            className="movingforward_section_button movingforward_section_button--inperson"
                            onClick={() => {
                                openCalendlyWidget('https://calendly.com/trishnelson-sd/50-minute-in-person-meeting');
                                return false;
                            }}
                            disabled={!calendlyReady}
                            title={!calendlyReady ? 'Loading scheduling widget...' : 'Schedule an in-person session'}
                        >
                            Session in Person
                        </button>
                    </div>
                </div>

                {/* Christ Church Network Section */}
                <div className="movingforward_section_card">
                    <h3 className="movingforward_section_subtitle">Christ Church Network</h3>
                    <p className="movingforward_section_text">
                        If you are part of the Christ Church network, you will be taken to a Calendly page to plan your Spiritual Direction session and pay.
                    </p>
                    
                    <div className="movingforward_section_buttons">
                        <button 
                            className="movingforward_section_button movingforward_section_button--christchurch"
                            onClick={() => {
                                openCalendlyWidget('https://calendly.com/trishnelson-sd/50-minute-christ-church-meeting');
                                return false;
                            }}
                            disabled={!calendlyReady}
                            title={!calendlyReady ? 'Loading scheduling widget...' : 'Schedule a Christ Church session'}
                        >
                            Session in Person
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
  }
  

  if (sectionType === 'splash') {
    return <SplashSection />;
  } else if (sectionType === 'welcome') {
    return <WelcomeSection />;
  } else if (sectionType === 'qualifications') {
    return <QualificationsSection />;
  } else if (sectionType === 'letsmeet') {
    return <LetsMeetSection />;
  } else if (sectionType === 'movingforward') {
    return <MovingForwardSection />;
  } else {
    return <div className="section">{children}</div>;
  }

};

export default Section;